import React, { useState } from "react";
import { Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import styled from "styled-components";
import DownloadIcon from "@mui/icons-material/Download";
import Page from "../../organisms/Page";
import { BreadCrumbItem } from "../../organisms/HeaderSection";
import LoadingScreen from "../../organisms/LoadingScreen";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { obtenerStringDeFecha } from "../../../utils/dateUtils";
import { downloadBlob } from "../../../utils/downloadUtils";
import {
  PlanesActivosPorDia as PlanesActivosPorDiaItem,
  useDescargarPlanesActivosGuarderiaPorDia,
  useObtenerPlanesActivosGuarderiaPorDia,
} from "../../../services/analisisOperaciones";
import PlanesActivosPorDiaChart from "./PlanesActivosPorDiaChart";

const breadCrumbs: BreadCrumbItem[] = [
  { path: "/", label: "Inicio" },
  { path: "/analisis", label: "Análisis" },
  { path: "/analisis/operaciones", label: "Operaciones" },
  {
    path: "/analisis/operaciones/planes-activos-por-dia",
    label: "Planes activos por día",
    isActive: true,
  },
];

function PlanesActivosPorDia() {
  const [fechaInicio, setFechaInicio] = useState<string>(() => {
    const fecha = new Date();
    fecha.setDate(1);
    fecha.setMonth(fecha.getMonth() - 1);
    return obtenerStringDeFecha(fecha);
  });

  const [fechaFin, setFechaFin] = useState<string>(() => {
    return obtenerStringDeFecha(new Date());
  });

  const {
    data: planesGuarderiaData,
    isLoading: isLoadingGuarderia,
    error: guarderiaError,
  } = useObtenerPlanesActivosGuarderiaPorDia(fechaInicio, fechaFin);

  const descargaGuarderiaMutacion = useDescargarPlanesActivosGuarderiaPorDia();

  const planesGuarderiaPorDia: PlanesActivosPorDiaItem[] = planesGuarderiaData || [];
  const isLoading = isLoadingGuarderia;
  const error = guarderiaError;

  const descargarGuarderia = async () => {
    const respuesta = await descargaGuarderiaMutacion.mutateAsync({
      fechaInicio,
      fechaFin,
    });
    const nombreArchivo = respuesta.nombre
      ? `${respuesta.nombre}${respuesta.extension ? `.${respuesta.extension}` : ""}`
      : `guarderia_asistencias_planes_por_dia_${fechaInicio}_a_${fechaFin}.xlsx`;
    downloadBlob(respuesta.contenido, nombreArchivo);
  };

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <Page
      title="Planes activos por día"
      description="Planes activos por fecha y tipo de servicio."
      breadCrumbs={breadCrumbs}
    >
      <LoadingScreen isLoading={isLoading} />
      <Row className="justify-content-center">
        <Col xs={12} md={12} lg={11} xxl={10}>
          <StyledCard>
            <Card.Body>
              <Row className="mb-3 g-3">
                <Col xs={12} md={4}>
                  <Form.Group controlId="fechaInicio">
                    <Form.Label>Fecha inicio</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaInicio}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFechaInicio(e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group controlId="fechaFin">
                    <Form.Label>Fecha fin</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaFin}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFechaFin(e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-end">
                  <ButtonsContainer>
                    <Button
                      variant="primary"
                      onClick={descargarGuarderia}
                      disabled={descargaGuarderiaMutacion.isLoading}
                    >
                      {descargaGuarderiaMutacion.isLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Descargando...
                        </>
                      ) : (
                        <>
                          <DownloadIcon sx={{ fontSize: 20, marginRight: "8px" }} />
                          Guardería
                        </>
                      )}
                    </Button>
                  </ButtonsContainer>
                </Col>
              </Row>
              {!isLoading && (
                <PlanesActivosPorDiaChart planesGuarderiaPorDia={planesGuarderiaPorDia} />
              )}
            </Card.Body>
          </StyledCard>
        </Col>
      </Row>
    </Page>
  );
}

const StyledCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export default PlanesActivosPorDia;
